import {
  ChakraVStack,
  FAIconName,
  FireflyButton,
  ModalV2,
  Text,
  useToastMessage,
  MultiSelectOption,
  ChakraBox,
} from '@equip.health/ui';
import AppointmentCancelReason, {
  CancellationReasonOption,
} from '~/components/maud/calendar/schedule/modals/AppointmentCancelReason';
import { RecurringAppointmentUpdateOption } from '~/lib/constants/calendar.constants';
import { useCancelAppointmentMutation } from '~/store/services/appointment';
import { useMemo, useState } from 'react';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { InquiryEvents } from '~/analytics/inquiryEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

interface CancelAppointmentWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointmentExternalId: string;
  inquiryExternalId: string;
}

const TOP_LEVEL_ID = 'AC-cancel-appointment-modal-v2';

const CancelAppointmentWarningModal = ({
  isOpen,
  onClose,
  appointmentExternalId,
  inquiryExternalId,
}: CancelAppointmentWarningModalProps) => {
  const [selectedReason, setSelectedReason] =
    useState<CancellationReasonOption>();
  const [selectedSubReason, setSelectedSubReason] =
    useState<MultiSelectOption>();

  const [
    deleteAppointment,
    { isLoading: isCancelAppointmentLoading, isError: isCancelError },
  ] = useCancelAppointmentMutation();

  const { createToast } = useToastMessage();
  const { trackEvent } = useAnalytics();

  const onConfirmCancel = () => {
    const showToast = (type: 'success' | 'critical') => {
      createToast({
        icon:
          type === 'success'
            ? FAIconName.checkRegular
            : FAIconName.timesRegular,
        id: `cancel-appointment-${type}-toast`,
        text:
          type === 'success'
            ? 'Cancelled appointment successfully.'
            : 'Failed to cancel appointment.',
        variant: type,
      });
    };

    deleteAppointment({
      cancelType: RecurringAppointmentUpdateOption.ONLY,
      appointmentExternalId,
      cancellationReason: selectedReason?.value,
      cancellationSubReason: selectedSubReason?.value,
      inquiryExternalId,
    })
      .unwrap()
      .then(() => {
        trackEvent(InquiryEvents.admissionConsultationCancelled, {
          inquiryId: inquiryExternalId,
          source: AnalyticsSourceValues.INQUIRY_ADMISSIONS_CONSULTATION,
          appointmentExternalId,
          cancellationReason: selectedReason?.value,
          cancellationSubReason: selectedSubReason?.value,
        });
        showToast('success');
        onClose();
      });
  };

  const shouldDisableCancelButton = useMemo(() => {
    if (selectedReason?.subReasons?.length > 0) return !selectedSubReason;

    return !selectedReason;
  }, [selectedReason, selectedSubReason]);

  return (
    <ModalV2
      isOpen={isOpen}
      onClose={onClose}
      title="Cancel this admission consultation?"
    >
      <ChakraVStack align="start" gap="24px">
        <Text variant="body" color="neutral.primary">
          Rebook this patient by adding a new appointment after cancellation.
        </Text>
        <ChakraBox width="100%">
          <AppointmentCancelReason
            id={TOP_LEVEL_ID}
            selectedReason={selectedReason}
            setSelectedReason={setSelectedReason}
            selectedSubReason={selectedSubReason}
            setSelectedSubReason={setSelectedSubReason}
            isCancelError={isCancelError}
          />
        </ChakraBox>
        <FireflyButton
          id="confirm-cancellation-button"
          onClick={onConfirmCancel}
          isFullWidth
          isLoading={isCancelAppointmentLoading}
          variant="accent.critical"
          isDisabled={shouldDisableCancelButton}
        >
          Confirm cancellation
        </FireflyButton>
      </ChakraVStack>
    </ModalV2>
  );
};

export default CancelAppointmentWarningModal;
